import { useState } from 'react';
import api from '../../api/client';

const C = {
  navy: '#1B2A4A', burgundy: '#7A2E3B', gold: '#B8953E', hunter: '#2D5F4A',
  ink: '#1A1A1A', ivory: '#FAF7F2', cream: '#F3EDE4',
};

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <div className={`bg-ivory-50 border border-stone-300 rounded-sm ${className}`}>{children}</div>;
}

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DC','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME',
  'MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI',
  'SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];

const ENTITY_TYPES = [
  { value: 'family_lp', label: 'Family Limited Partnership' },
  { value: 'family_llc', label: 'Family LLC' },
  { value: 'revocable_trust', label: 'Revocable Trust' },
  { value: 'irrevocable_trust', label: 'Irrevocable Trust' },
  { value: 'grat', label: 'GRAT' },
  { value: 'idgt', label: 'IDGT' },
  { value: 'ilit', label: 'ILIT' },
  { value: 's_corp', label: 'S Corporation' },
  { value: 'c_corp', label: 'C Corporation' },
  { value: 'foundation', label: 'Private Foundation' },
  { value: 'other', label: 'Other' },
];

const ASSET_TYPES = [
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'business_interest', label: 'Business Interest / Operating Company' },
  { value: 'public_equity', label: 'Public Equity / Stocks' },
  { value: 'private_equity', label: 'Private Equity / Alternatives' },
  { value: 'fixed_income', label: 'Fixed Income / Bonds' },
  { value: 'cash', label: 'Cash & Equivalents' },
  { value: 'insurance', label: 'Life Insurance' },
  { value: 'other', label: 'Other' },
];

interface FamilyMember {
  firstName: string; lastName: string; relationship: string;
  dateOfBirth: string; stateOfResidence: string;
}
interface Entity {
  name: string; entityType: string; domicileState: string; isGrantorTrust: boolean;
}
interface Asset {
  name: string; assetType: string; currentValue: number; entityName: string;
}

export default function OnboardingWizard() {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [complete, setComplete] = useState(false);

  // Step data
  const [members, setMembers] = useState<FamilyMember[]>([
    { firstName: '', lastName: '', relationship: 'patriarch', dateOfBirth: '', stateOfResidence: 'OK' },
  ]);
  const [entities, setEntities] = useState<Entity[]>([
    { name: '', entityType: 'family_lp', domicileState: 'OK', isGrantorTrust: false },
  ]);
  const [assets, setAssets] = useState<Asset[]>([
    { name: '', assetType: 'real_estate', currentValue: 0, entityName: '' },
  ]);

  const steps = [
    { label: 'Family Members', desc: 'Who are the key people in this plan?' },
    { label: 'Entities', desc: 'Trusts, LLCs, partnerships, and corporations' },
    { label: 'Assets', desc: 'Holdings across all entities' },
    { label: 'Review & Save', desc: 'Confirm everything looks right' },
  ];

  const addMember = () => setMembers([...members, { firstName: '', lastName: '', relationship: 'child', dateOfBirth: '', stateOfResidence: 'OK' }]);
  const removeMember = (i: number) => setMembers(members.filter((_, idx) => idx !== i));
  const updateMember = (i: number, field: string, value: string) => {
    const updated = [...members]; (updated[i] as any)[field] = value; setMembers(updated);
  };

  const addEntity = () => setEntities([...entities, { name: '', entityType: 'family_llc', domicileState: 'OK', isGrantorTrust: false }]);
  const removeEntity = (i: number) => setEntities(entities.filter((_, idx) => idx !== i));
  const updateEntity = (i: number, field: string, value: any) => {
    const updated = [...entities]; (updated[i] as any)[field] = value; setEntities(updated);
  };

  const addAsset = () => setAssets([...assets, { name: '', assetType: 'real_estate', currentValue: 0, entityName: '' }]);
  const removeAsset = (i: number) => setAssets(assets.filter((_, idx) => idx !== i));
  const updateAsset = (i: number, field: string, value: any) => {
    const updated = [...assets]; (updated[i] as any)[field] = value; setAssets(updated);
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      // Save members
      for (const m of members.filter(m => m.firstName)) {
        await api.post('/members', {
          firstName: m.firstName, lastName: m.lastName,
          relationship: m.relationship, dateOfBirth: m.dateOfBirth || null,
          stateOfResidence: m.stateOfResidence,
        });
      }

      // Save entities
      const entityMap: Record<string, string> = {};
      for (const e of entities.filter(e => e.name)) {
        const res = await api.post('/entities', {
          name: e.name, entityType: e.entityType,
          domicileState: e.domicileState, isGrantorTrust: e.isGrantorTrust,
        });
        entityMap[e.name] = res.data.data.id;
      }

      // Save assets
      for (const a of assets.filter(a => a.name && a.currentValue > 0)) {
        await api.post('/assets', {
          name: a.name, assetType: a.assetType,
          currentValue: a.currentValue,
          entityId: a.entityName ? entityMap[a.entityName] : null,
        });
      }

      setComplete(true);
    } catch (err) {
      console.error('Onboarding save failed:', err);
      alert('Some items failed to save. Please check your data and try again.');
    }
    setSaving(false);
  };

  if (complete) {
    return (
      <Card className="p-12 text-center max-w-2xl mx-auto mt-12">
        <div className="text-4xl mb-4">✓</div>
        <h2 className="text-xl font-display font-semibold text-navy-800 mb-2">Setup Complete</h2>
        <p className="text-[14px] font-sans text-stone-500 mb-6">
          Your family office data has been saved. You can now explore all modules — Dashboard, Liquidity Runway,
          Estate Tax Analysis, Strategy Workbench, and more.
        </p>
        <a href="/" className="inline-block px-6 py-3 rounded-sm text-[13px] font-sans font-bold text-ivory-100" style={{ background: C.navy }}>
          Go to Dashboard
        </a>
      </Card>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Progress */}
      <div className="flex gap-0 mb-8">
        {steps.map((s, i) => (
          <div key={i} className="flex items-center">
            <div onClick={() => i <= step && setStep(i)}
              className={`flex items-center gap-2 px-4 py-2 rounded-sm cursor-pointer transition-all ${step === i ? 'text-ivory-100' : step > i ? 'text-emerald-700 bg-emerald-50' : 'text-stone-400 bg-transparent'}`}
              style={step === i ? { background: C.navy } : {}}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${
                step === i ? 'bg-ivory-100 text-navy-800' : step > i ? 'bg-emerald-600 text-white' : 'bg-stone-200 text-stone-400'
              }`}>{step > i ? '✓' : i + 1}</div>
              <span className="text-[12px] font-sans font-semibold">{s.label}</span>
            </div>
            {i < 3 && <div className="w-6 h-px bg-stone-300" />}
          </div>
        ))}
      </div>

      {/* Step Content */}
      <Card className="p-6">
        <h2 className="text-lg font-display font-semibold text-navy-800 mb-1">{steps[step].label}</h2>
        <p className="text-[13px] font-sans text-stone-500 mb-6">{steps[step].desc}</p>

        {/* STEP 0: Members */}
        {step === 0 && (
          <div>
            {members.map((m, i) => (
              <div key={i} className="grid grid-cols-5 gap-3 mb-3 p-3 bg-ivory-200 rounded-sm">
                <input placeholder="First name" value={m.firstName} onChange={e => updateMember(i, 'firstName', e.target.value)}
                  className="px-3 py-2 bg-white border border-stone-300 rounded-sm text-[13px] font-sans" />
                <input placeholder="Last name" value={m.lastName} onChange={e => updateMember(i, 'lastName', e.target.value)}
                  className="px-3 py-2 bg-white border border-stone-300 rounded-sm text-[13px] font-sans" />
                <select value={m.relationship} onChange={e => updateMember(i, 'relationship', e.target.value)}
                  className="px-3 py-2 bg-white border border-stone-300 rounded-sm text-[13px] font-sans">
                  {['patriarch', 'spouse', 'child', 'grandchild', 'sibling', 'other'].map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <select value={m.stateOfResidence} onChange={e => updateMember(i, 'stateOfResidence', e.target.value)}
                  className="px-3 py-2 bg-white border border-stone-300 rounded-sm text-[13px] font-sans">
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                {i > 0 && <button onClick={() => removeMember(i)} className="text-[12px] text-red-600 hover:text-red-800">Remove</button>}
              </div>
            ))}
            <button onClick={addMember} className="text-[12px] font-sans font-bold text-navy-800 hover:text-navy-600 mt-2">+ Add Family Member</button>
          </div>
        )}

        {/* STEP 1: Entities */}
        {step === 1 && (
          <div>
            {entities.map((e, i) => (
              <div key={i} className="grid grid-cols-4 gap-3 mb-3 p-3 bg-ivory-200 rounded-sm">
                <input placeholder="Entity name" value={e.name} onChange={ev => updateEntity(i, 'name', ev.target.value)}
                  className="px-3 py-2 bg-white border border-stone-300 rounded-sm text-[13px] font-sans" />
                <select value={e.entityType} onChange={ev => updateEntity(i, 'entityType', ev.target.value)}
                  className="px-3 py-2 bg-white border border-stone-300 rounded-sm text-[13px] font-sans">
                  {ENTITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <select value={e.domicileState} onChange={ev => updateEntity(i, 'domicileState', ev.target.value)}
                  className="px-3 py-2 bg-white border border-stone-300 rounded-sm text-[13px] font-sans">
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-1.5 text-[12px] font-sans text-stone-600">
                    <input type="checkbox" checked={e.isGrantorTrust} onChange={ev => updateEntity(i, 'isGrantorTrust', ev.target.checked)} />
                    Grantor Trust
                  </label>
                  {i > 0 && <button onClick={() => removeEntity(i)} className="text-[12px] text-red-600 ml-auto">Remove</button>}
                </div>
              </div>
            ))}
            <button onClick={addEntity} className="text-[12px] font-sans font-bold text-navy-800 hover:text-navy-600 mt-2">+ Add Entity</button>
          </div>
        )}

        {/* STEP 2: Assets */}
        {step === 2 && (
          <div>
            {assets.map((a, i) => (
              <div key={i} className="grid grid-cols-4 gap-3 mb-3 p-3 bg-ivory-200 rounded-sm">
                <input placeholder="Asset name" value={a.name} onChange={e => updateAsset(i, 'name', e.target.value)}
                  className="px-3 py-2 bg-white border border-stone-300 rounded-sm text-[13px] font-sans" />
                <select value={a.assetType} onChange={e => updateAsset(i, 'assetType', e.target.value)}
                  className="px-3 py-2 bg-white border border-stone-300 rounded-sm text-[13px] font-sans">
                  {ASSET_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <input type="number" placeholder="Current value" value={a.currentValue || ''} onChange={e => updateAsset(i, 'currentValue', Number(e.target.value))}
                  className="px-3 py-2 bg-white border border-stone-300 rounded-sm text-[13px] font-mono" />
                <div className="flex items-center gap-2">
                  <select value={a.entityName} onChange={e => updateAsset(i, 'entityName', e.target.value)}
                    className="flex-1 px-3 py-2 bg-white border border-stone-300 rounded-sm text-[13px] font-sans">
                    <option value="">No entity</option>
                    {entities.filter(e => e.name).map((e, ei) => <option key={ei} value={e.name}>{e.name}</option>)}
                  </select>
                  {i > 0 && <button onClick={() => removeAsset(i)} className="text-[12px] text-red-600">Remove</button>}
                </div>
              </div>
            ))}
            <button onClick={addAsset} className="text-[12px] font-sans font-bold text-navy-800 hover:text-navy-600 mt-2">+ Add Asset</button>
          </div>
        )}

        {/* STEP 3: Review */}
        {step === 3 && (
          <div className="space-y-5">
            <div>
              <h3 className="text-[12px] font-sans font-bold text-stone-400 tracking-wide uppercase mb-2">Family Members ({members.filter(m => m.firstName).length})</h3>
              {members.filter(m => m.firstName).map((m, i) => (
                <div key={i} className="text-[13px] font-sans text-stone-700 py-1">{m.firstName} {m.lastName} — {m.relationship} ({m.stateOfResidence})</div>
              ))}
            </div>
            <div>
              <h3 className="text-[12px] font-sans font-bold text-stone-400 tracking-wide uppercase mb-2">Entities ({entities.filter(e => e.name).length})</h3>
              {entities.filter(e => e.name).map((e, i) => (
                <div key={i} className="text-[13px] font-sans text-stone-700 py-1">{e.name} — {e.entityType.replace(/_/g, ' ')} ({e.domicileState})</div>
              ))}
            </div>
            <div>
              <h3 className="text-[12px] font-sans font-bold text-stone-400 tracking-wide uppercase mb-2">Assets ({assets.filter(a => a.name && a.currentValue > 0).length})</h3>
              {assets.filter(a => a.name && a.currentValue > 0).map((a, i) => (
                <div key={i} className="flex justify-between text-[13px] font-sans text-stone-700 py-1">
                  <span>{a.name} — {a.assetType.replace(/_/g, ' ')}</span>
                  <span className="font-mono font-semibold">${a.currentValue.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-8 pt-5 border-t border-stone-200">
          {step > 0 ? (
            <button onClick={() => setStep(step - 1)} className="px-5 py-2.5 rounded-sm text-[12px] font-sans font-bold border border-stone-300 text-stone-600 bg-ivory-200 hover:bg-stone-200">
              Back
            </button>
          ) : <div />}

          {step < 3 ? (
            <button onClick={() => setStep(step + 1)} className="px-5 py-2.5 rounded-sm text-[12px] font-sans font-bold text-ivory-100" style={{ background: C.navy }}>
              Continue
            </button>
          ) : (
            <button onClick={handleSaveAll} disabled={saving}
              className="px-6 py-2.5 rounded-sm text-[13px] font-sans font-bold text-ivory-100 disabled:opacity-50" style={{ background: C.hunter }}>
              {saving ? 'Saving...' : 'Save & Complete Setup'}
            </button>
          )}
        </div>
      </Card>
    </div>
  );
}
